import { Platform } from 'react-native';
import { contrastRatio } from '../utils/colorShade';

/**
 * CORE-1733 — акцентный текст и значимые глифы: оранжевый как ЦВЕТ ТЕКСТА
 * (активный пункт навигации, вкладка, подпись, заголовок-акцент, иконка рядом
 * с подписью), а не заливка.
 *
 * Фирменный #E98245 на сливочном фоне v4 даёт 2.2–2.5:1 — не проходит ни 4.5
 * для обычного текста, ни 3 для крупного и глифов. Статья 110756133 задаёт для
 * «небольших акцентных подписей» светлой темы #9A421F (5.0–6.6 на странице,
 * шапке, поверхностях и оранжевых подложках чипов). В тёмной #E98245 проседает
 * до 4.4 в тёплом углу поверхности и на выбранной поверхности, поэтому там —
 * #F0A06D из той же палитры статьи (5.6–7.5).
 *
 * На вебе значение — CSS-переменная: её задаёт public/global.css по теме
 * (`html[data-theme]` и системная тёмная до JS), поэтому токен работает и в
 * статичных StyleSheet, и в атрибутах SVG, и переключается вместе с темой без
 * перерисовки. Заливки CTA, бренд-градиенты, иллюстрации и сцена Live этот
 * токен не используют. Натив — прежний фирменный оранжевый.
 */
export const ACCENT_TEXT_HEX = {
  light: '#9A421F',
  dark: '#F0A06D',
} as const;

export const ACCENT_TEXT: string = Platform.OS === 'web' ? 'var(--ag-accent-text, #9A421F)' : '#E98245';

/**
 * CORE-1733 (возврат QA 22.10) — текст и значимые глифы НА акцентной заливке:
 * кнопки и плашки фирменного оранжевого, CTA-градиент, выбранные чипы и даты.
 *
 * Белый на #E98245 даёт 2.71:1 (и ниже на светлых стопах CTA-градиента) — не
 * проходит 4.5 ни в одной теме. Дизайн-система задаёт на оранжевых заливках
 * тёмный текст: основная кнопка оверлеев Live (статья 111280168,
 * `.live-overlay__primary`) — #332A25, 5.1:1 на ровном #E98245.
 *
 * Возврат QA 23.10: на вебе ровная оранжевая кнопка платформы перекрашивается
 * в CTA-градиент (public/global.css, `--gradient-cta`), и первый стоп #F45122
 * с #332A25 даёт 4.03:1 — первые буквы подписи были 4.18–4.48. Поэтому тот же
 * тёплый тёмно-коричневый, но глубже: #221A16 держит 4.93 у самого края
 * первого стопа, 6.31 на #E98245, 4.7 на старте градиента наведения. Оверлеи
 * Live и кнопка отправки сообщений (ровная заливка) остаются на своём #332A25.
 * Значение одно для обеих тем: заливка от темы не зависит. Натив — прежний белый.
 */
export const ON_ACCENT_TEXT_HEX = '#221A16';
export const ON_ACCENT_TEXT: string = Platform.OS === 'web' ? ON_ACCENT_TEXT_HEX : '#FFFFFF';

/**
 * CORE-1733 (возврат QA 23.10) — подпись на цветной заливке, цвет которой
 * приходит данными (статус комнаты, категория): из белого и тёмного
 * ON_ACCENT_TEXT — тот, что контрастнее на этой заливке. Бейдж «СКОРО» на
 * янтарном #F59E0B белым давал 2.15:1. Решает контраст, а не список цветов —
 * новый статус со своим цветом не вернёт дефект. Натив — прежний белый.
 *
 * Возврат QA 27.10: прежнее правило «белый, если держит 4.5, иначе тёмный» на
 * заливках средней яркости выбирало худший из двух — #8B5CF6 4.04 вместо 4.23,
 * #2D8A4E 3.96 вместо 4.32, #E91E8C 4.10 вместо 4.18. Ни один там не держит
 * 4.5, но подпись берёт лучший. Нераспознанный цвет — тёмный, как раньше.
 */
export function inkOnFill(fill: string): string {
  if (Platform.OS !== 'web') return '#FFFFFF';
  if (!/^#[0-9a-f]{6}$/i.test(fill)) return ON_ACCENT_TEXT_HEX;
  return contrastRatio('#FFFFFF', fill) >= contrastRatio(ON_ACCENT_TEXT_HEX, fill) ? '#FFFFFF' : ON_ACCENT_TEXT_HEX;
}

/**
 * CORE-1733 (возврат QA 23.10, замечание ревью 58868) — отмеченный чекбокс
 * согласий: фирменная оранжевая заливка с тёмной галочкой, одна для обеих тем и
 * для обоих чекбоксов регистрации. Раньше заливку брали из цвета ссылки: светлая
 * #9A421F (коричневый), тёмная #F0A06D с белой галочкой 2.11:1, а рекламный
 * чекбокс под ним — #F25C05.
 */
export const CHECKBOX_ON_FILL = '#E98245';

/**
 * CORE-1733 (возврат QA) — приглушённый текст цветом, а не прозрачностью.
 *
 * Подписи, набранные основным цветом с opacity .25–.7, на слоях v4 давали
 * 1.4–3.4:1 (тот же дефект, что у пунктов шапки). На вебе — CSS-переменная
 * `--text-muted` (= textMuted темы, ≥4.5 на всех слоях, в обеих темах), на
 * нативе — прежняя прозрачность: натив эта задача не меняет.
 */
export function mutedText(nativeOpacity: number): { color: string } | { opacity: number } {
  return Platform.OS === 'web' ? { color: 'var(--text-muted, #686170)' } : { opacity: nativeOpacity };
}

/**
 * Оранжевый ли это фирменного семейства (тон 14–30°, насыщенный, средней
 * светлоты) — по нему цвет категории или пропа, пришедший извне, переводится
 * в акцентный текст там, где он рисует текст или глиф. Красно-оранжевые
 * (деструктивные) и янтарь/золото сюда не входят.
 */
export function isBrandOrange(color: string): boolean {
  const m = /^#([0-9a-f]{6})$/i.exec(color);
  if (!m) return false;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(m[1].slice(i, i + 2), 16) / 255);
  const mx = Math.max(r, g, b);
  const mn = Math.min(r, g, b);
  const l = (mx + mn) / 2;
  const d = mx - mn;
  if (d < 0.08) return false;
  const s = d / (1 - Math.abs(2 * l - 1));
  let h = mx === r ? ((g - b) / d) % 6 : mx === g ? (b - r) / d + 2 : (r - g) / d + 4;
  h *= 60;
  if (h < 0) h += 360;
  return h >= 14 && h <= 30 && s >= 0.5 && l >= 0.38 && l <= 0.72;
}

/**
 * Акцентный текст на вебе, прежний цвет на нативе. Перевод на токен касается
 * веба (CORE-1733 — FE-Web): там, где раньше стоял не фирменный #E98245, а свой
 * оттенок (#E85D04, ctaOutline, цвет из пропа), натив сохраняет его как был.
 */
export function accentTextOn(nativeColor: string): string {
  return Platform.OS === 'web' ? ACCENT_TEXT : nativeColor;
}

/** Цвет для текста или глифа: фирменный оранжевый → ACCENT_TEXT на вебе, остальное как есть. */
export function accentInk(color: string): string {
  return Platform.OS === 'web' && isBrandOrange(color) ? ACCENT_TEXT : color;
}
